// ---------------------------------------------------------------------------
// Telecaller mode — wire types shared by the hook, the overlay and the lead
// header launcher. Mirrors src/ai/voice/live.socket.ts (server); keep in sync.
// ---------------------------------------------------------------------------

export type TelecallerObjective = "first_contact" | "follow_up" | "document_reminder" | "re_enquiry" | "intake_reminder" | "custom";

export const TELECALLER_OBJECTIVES: Array<{ value: TelecallerObjective; ml: string; en: string; hint: string }> = [
  { value: "first_contact", ml: "ആദ്യ കോൾ — enquiry qualify ചെയ്യുക", en: "First call — qualify the enquiry", hint: "Course, country, intake, IELTS, budget, decision-maker, next step." },
  { value: "follow_up", ml: "Follow-up — സംസാരം തുടരുക", en: "Follow-up — continue the conversation", hint: "Refers to the last note, finds what they decided, fixes the next step." },
  { value: "document_reminder", ml: "Documents ഓർമ്മിപ്പിക്കൽ", en: "Document reminder", hint: "Only the papers the brief names; agrees a date and a channel." },
  { value: "re_enquiry", ml: "Re-enquiry — വീണ്ടും വന്നത് എന്തിന്", en: "Re-enquiry — why they came back", hint: "No scolding, no old rejection; finds what changed." },
  { value: "intake_reminder", ml: "Intake / deadline ഓർമ്മിപ്പിക്കൽ", en: "Intake / deadline reminder", hint: "States the intake from the brief; gets a yes / not-yet / no." },
  { value: "custom", ml: "Custom brief", en: "Custom brief", hint: "Type what this call is for." },
];

/**
 * The objectives worded for the tenant's industry — 1 study abroad (the list
 * above), 5 academy, 4 video production, 6 travel, anything else generic.
 * Mirrors voice/telecaller/vertical.ts on the server.
 */
export function telecallerObjectives(industryId: number | null | undefined): typeof TELECALLER_OBJECTIVES {
  const id = Number(industryId);
  if (id === 1) return TELECALLER_OBJECTIVES;
  const words =
    id === 5
      ? {
          first: "Course, batch timing, mode, start date, fee comfort, decision-maker, next step (demo class).",
          docs: { ml: "Admission documents ഓർമ്മിപ്പിക്കൽ", en: "Admission documents reminder", hint: "Only the papers the brief names; agrees a date and a channel." },
          date: { ml: "Batch start / deadline ഓർമ്മിപ്പിക്കൽ", en: "Batch start / deadline reminder", hint: "States the batch start or deadline from the brief; gets a yes / not-yet / no." },
        }
      : id === 4
        ? {
            first: "Shoot type, date, venue, deliverables, budget, decision-maker, next step (quote).",
            docs: { ml: "Details / documents ഓർമ്മിപ്പിക്കൽ", en: "Details / documents reminder", hint: "Only what the brief names; agrees a date and a channel." },
            date: { ml: "Date / deadline ഓർമ്മിപ്പിക്കൽ", en: "Date / deadline reminder", hint: "States the date or deadline from the brief; gets a yes / not-yet / no." },
          }
        : id === 6
          ? {
              first: "Destination, dates, travellers, budget, passport / visa, decision-maker, next step (itinerary).",
              docs: { ml: "Details / documents ഓർമ്മിപ്പിക്കൽ", en: "Details / documents reminder", hint: "Only what the brief names; agrees a date and a channel." },
              date: { ml: "Date / deadline ഓർമ്മിപ്പിക്കൽ", en: "Date / deadline reminder", hint: "States the date or deadline from the brief; gets a yes / not-yet / no." },
            }
          : {
              first: "What they need, when, budget, decision-maker, next step.",
              docs: { ml: "Details / documents ഓർമ്മിപ്പിക്കൽ", en: "Details / documents reminder", hint: "Only what the brief names; agrees a date and a channel." },
              date: { ml: "Date / deadline ഓർമ്മിപ്പിക്കൽ", en: "Date / deadline reminder", hint: "States the date or deadline from the brief; gets a yes / not-yet / no." },
            };
  return TELECALLER_OBJECTIVES.map((o) =>
    o.value === "first_contact" ? { ...o, hint: words.first } : o.value === "document_reminder" ? { ...o, ...words.docs } : o.value === "intake_reminder" ? { ...o, ...words.date } : o,
  );
}

/** `ai:voice:start` payload for a telecaller call (assistant calls send only `language`). */
export type TelecallerStartOptions =
  | { mode: "assistant" }
  | {
      mode: "telecaller";
      leadId: number;
      objective: TelecallerObjective;
      brief?: string;
      agentName?: string;
      /** "female" | "male" | a Gemini voice name. */
      voice?: string;
      discloseAI?: boolean;
      allowOutsideHours?: boolean;
      allowDoNotCall?: boolean;
      /** Rehearsal: the launcher plays the lead; the AI behaves identically; nothing is saved. */
      practice?: boolean;
      /** Malayalam flavour: auto = by the lead's branch (Malabar → Mappila register). */
      dialect?: "auto" | "mappila" | "kozhikode" | "standard";
      /** Opening greeting for the Mappila flavour: auto = by the lead's name. */
      greeting?: "auto" | "salaam" | "hello";
      /** The company name exactly as the AI should SAY it (defaults to the tenant name). */
      companyName?: string;
      /** Pronunciation hint for the company name, e.g. "SKY-mark". */
      companySayAs?: string;
      /** Speaking tempo: "brisk" (the Kozhikode default — people talk fast), "natural" or "fast". */
      pace?: "natural" | "brisk" | "fast";
      /** Who speaks: Gemini's own voice, or Sarvam / ElevenLabs re-voicing Gemini's words. */
      engine?: VoiceEngineId;
      /** The Sarvam speaker / ElevenLabs voice id (engine ≠ gemini). */
      engineVoice?: string;
    };

export type VoiceEngineId = "gemini" | "sarvam" | "elevenlabs";

/** GET /ai/voice/engines — one engine and the voices this workspace may use. */
export interface VoiceEngineVoice {
  id: string;
  name: string;
  gender: "female" | "male" | null;
  /** A cloned / professional voice (ElevenLabs), not a stock one. */
  custom: boolean;
  previewUrl: string | null;
  description?: string | null;
  accent?: string | null;
}
export interface VoiceEngineInfo {
  id: VoiceEngineId;
  label: string;
  model: string;
  /** false = no usable key for this workspace (its own, or Xale's). */
  available: boolean;
  canClone: boolean;
  /** Sarvam / ElevenLabs: whose account the call uses. */
  keySource?: VoiceKeySource;
  voices: VoiceEngineVoice[];
  error?: string;
}

export type VoiceKeySource = "xale" | "own";
export type VoiceProviderId = "sarvam" | "elevenlabs";

/** The voice every AI call starts with (Settings → AI Connections). voice null = the engine's default. */
export interface VoiceDefaults {
  engine: VoiceEngineId;
  voice: string | null;
}

/** GET /ai/voice/engines. `defaults` is absent on an older server. */
export interface VoiceEnginesResponse {
  engines: VoiceEngineInfo[];
  defaults?: VoiceDefaults;
}

/** GET|PATCH /ai/voice/settings — never a key, only its last 4 characters. */
export interface VoiceProviderSettings {
  keySource: VoiceKeySource;
  hasOwnKey: boolean;
  ownKeyLast4: string | null;
  /** Xale's account is set up on this server. */
  xaleAvailable: boolean;
  /** A call can use this provider right now. */
  connected: boolean;
}
export interface VoiceSettings {
  defaultEngine: VoiceEngineId;
  defaultVoice: string | null;
  providers: Record<VoiceProviderId, VoiceProviderSettings>;
}
export interface VoiceSettingsPatch {
  defaultEngine?: VoiceEngineId;
  defaultVoice?: string | null;
  sarvam?: { keySource?: VoiceKeySource; apiKey?: string | null };
  elevenlabs?: { keySource?: VoiceKeySource; apiKey?: string | null };
}

/** Gemini voices offered for calls: Google's descriptors, genders per the Chirp 3 HD table. */
export const GEMINI_CALL_VOICES: Array<{ value: string; ml: string; en: string; male?: boolean }> = [
  { value: "female", ml: "സ്ത്രീ — warm (Sulafat)", en: "Female — warm (Sulafat)" },
  { value: "Despina", ml: "സ്ത്രീ — smooth (Despina)", en: "Female — smooth (Despina)" },
  { value: "Leda", ml: "സ്ത്രീ — youthful (Leda)", en: "Female — youthful (Leda)" },
  { value: "Zephyr", ml: "സ്ത്രീ — bright (Zephyr)", en: "Female — bright (Zephyr)" },
  { value: "Laomedeia", ml: "സ്ത്രീ — upbeat (Laomedeia)", en: "Female — upbeat (Laomedeia)" },
  { value: "Aoede", ml: "സ്ത്രീ — breezy (Aoede)", en: "Female — breezy (Aoede)" },
  { value: "male", ml: "പുരുഷൻ — friendly (Achird)", en: "Male — friendly (Achird)", male: true },
  { value: "Algieba", ml: "പുരുഷൻ — smooth (Algieba)", en: "Male — smooth (Algieba)", male: true },
  { value: "Puck", ml: "പുരുഷൻ — upbeat (Puck)", en: "Male — upbeat (Puck)", male: true },
];

/** `ai:voice:telecaller` — sent once the line is up. */
export interface TelecallerHello {
  lead: {
    id: number;
    name: string;
    stage: string;
    status: string;
    subStatus: string;
    source: string | null;
    reenquired: number;
  };
  branch: { name: string } | null;
  assignees: Array<{ name: string; role: string | null }>;
  fields: Record<string, string>;
  deals: Array<{ id: number; name: string; stage: string; status: string }>;
  pendingFollowUps: number;
  calls: number;
  doNotCall: { at: string | null; reason: string | null } | null;
  objective: TelecallerObjective;
  label: string;
  window: { open: boolean; label: string };
  agentName: string;
  discloseAI: boolean;
  voice: string;
  /** Who speaks on this call (older servers omit it = gemini). */
  engine?: VoiceEngineId;
  language: "ml" | "en";
  practice?: boolean;
  /** The flavour the server resolved (auto → by branch). */
  dialect?: "mappila" | "kozhikode" | "standard";
  /** The greeting the server resolved (auto → by name; Mappila only). */
  greeting?: "salaam" | "hello";
  /** The speaking tempo in force (brisk unless the sheet said otherwise). */
  pace?: "natural" | "brisk" | "fast";
  /** The industry persona the server resolved from the tenant's industry. */
  vertical?: "study_abroad" | "academy" | "video" | "travel" | "generic";
  /** The human colleague's title in this industry: counsellor | executive. */
  human?: string;
}

/** Where the AI thinks the lead should now sit — a suggestion; a human applies it. */
export interface StatusSuggestion {
  stageId: number;
  stageName: string;
  statusId: number;
  statusName: string;
  kind: "rejection" | "qualifying" | "neutral";
  subStatusId: number | null;
  subStatusName: string | null;
}

/** `ai:voice:outcome` — what the AI wrote on the lead. */
export interface TelecallerOutcome {
  disposition: string;
  label: string;
  lead?: { id: number; name: string };
  suggestion?: StatusSuggestion | null;
  interest?: string | null;
  summary: string | null;
  keyFacts?: string[];
  nextStep?: string | null;
  noteId: number | null;
  followUp: { created: true; id: number; at: string } | { created: false; reason: string } | null;
  /** true when the model never logged and the transcript was saved instead. */
  fallback: boolean;
  reason?: string;
  /** Practice call: the outcome was NOT written anywhere. */
  practice?: boolean;
}
